import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { PillChip } from "@/components/toggles";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatError } from "@/lib/api";
import { ArrowLeft, Zap, FileText, BarChart3, Shield, Sparkles, Target, ChevronRight, Check, RefreshCw, Star, CreditCard } from "lucide-react";
import CreatorHelperTarifeTab from "@/components/studio/CreatorHelperTarifeTab";

const TABS = [
  { key: "overview", i18nKey: "creator_helper.tab_overview", fallback: "Übersicht", icon: BarChart3 },
  { key: "tariffs", i18nKey: "creator_helper.tab_tariffs", fallback: "Tarife / Abonnement", icon: CreditCard },
  { key: "audit", i18nKey: "creator_helper.tab_audit", fallback: "Profil-Audit", icon: Shield },
  { key: "textgen", i18nKey: "creator_helper.tab_textgen", fallback: "Text-Generator", icon: FileText },
  { key: "tags", i18nKey: "creator_helper.tab_tags", fallback: "Tag-Optimierung", icon: Target },
  { key: "score", i18nKey: "creator_helper.tab_score", fallback: "Content-Score", icon: Star },
  { key: "insights", i18nKey: "creator_helper.tab_insights", fallback: "Insights", icon: BarChart3 },
  { key: "wizard", i18nKey: "creator_helper.tab_wizard", fallback: "Coaching-Wizard", icon: Sparkles },
];

export default function CreatorHelperPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get("tab") || "overview";
  const [tab, setTab] = useState(initialTab);
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);

  const changeTab = (nextKey) => {
    setTab(nextKey);
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      if (nextKey === "overview") p.delete("tab");
      else p.set("tab", nextKey);
      return p;
    }, { replace: true });
  };

  useEffect(() => {
    if (!user || user === false) { navigate("/login"); return; }
    loadOverview();
  }, [user, navigate]);

  const loadOverview = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/studio/helper/overview");
      setOverview(data);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };

  if (loading) return <div className="flex justify-center py-16"><div className="w-6 h-6 border-2 border-[#E5E5E5] border-t-[#0A0A0A] rounded-full animate-spin" /></div>;

  return (
    <div data-testid="creator-helper-page">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-[#E5E5E5]">
        <button data-testid="helper-back" onClick={() => navigate("/studio")} className="text-[#0A0A0A]"><ArrowLeft size={20} /></button>
        <h1 className="text-base font-bold text-[#0A0A0A]">{t("creator_helper.title", { defaultValue: "Creator-Helper" })}</h1>
        {overview && (
          <span className="ml-auto px-2 py-0.5 bg-[#0A0A0A] text-white text-[10px] font-medium rounded-full">{overview.plan_code}</span>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 px-4 py-2 overflow-x-auto scrollbar-hide border-b border-[#E5E5E5]">
        {TABS.map((tabItem) => (
          <button key={tabItem.key} data-testid={`helper-tab-${tabItem.key}`} onClick={() => changeTab(tabItem.key)}
            className={`shrink-0 flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-full border transition-colors ${
              tab === tabItem.key ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-white text-[#666] border-[#E5E5E5]"
            }`}>
            <tabItem.icon size={12} /> {t(tabItem.i18nKey, { defaultValue: tabItem.fallback })}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="px-4 py-4">
        {tab === "overview" && <OverviewTab overview={overview} />}
        {tab === "tariffs" && <CreatorHelperTarifeTab />}
        {tab === "audit" && <AuditTab />}
        {tab === "textgen" && <TextGenTab overview={overview} />}
        {tab === "tags" && <TagsTab />}
        {tab === "score" && <ContentScoreTab />}
        {tab === "insights" && <InsightsTab />}
        {tab === "wizard" && <WizardTab />}
      </div>
    </div>
  );
}

function OverviewTab({ overview }) {
  const { t } = useTranslation();
  if (!overview) return null;
  const audit = overview.latest_audit;
  return (
    <div data-testid="helper-overview" className="space-y-4">
      {/* Plan Info */}
      <div className="p-4 border border-[#E5E5E5]">
        <p className="text-xs text-[#999] uppercase tracking-wider font-medium">{t("creator_helper.your_plan")}</p>
        <p className="text-lg font-bold text-[#0A0A0A] mt-1">{overview.plan_code}</p>
        <div className="mt-2 text-xs text-[#666]">
          <p>Text-Generierungen: {overview.usage.text_gen_used} / {overview.usage.text_gen_limit} diesen Monat</p>
          <div className="mt-1 h-1.5 bg-[#E5E5E5]">
            <div className="h-full bg-[#0A0A0A]" style={{ width: `${Math.min(100, (overview.usage.text_gen_used / overview.usage.text_gen_limit) * 100)}%` }} />
          </div>
        </div>
      </div>
      {/* Latest Audit */}
      {audit && (
        <div className="p-4 border border-[#E5E5E5]">
          <p className="text-xs text-[#999] uppercase tracking-wider font-medium">Letzter Profil-Audit</p>
          <div className="flex items-center gap-3 mt-2">
            <div className="w-14 h-14 rounded-full border-4 border-[#0A0A0A] flex items-center justify-center">
              <span className="text-lg font-bold text-[#0A0A0A]">{audit.total_score}</span>
            </div>
            <div>
              <p className="text-sm font-medium text-[#0A0A0A]">Potenzial: {audit.total_score}%</p>
              <p className="text-xs text-[#999]">{audit.weaknesses?.length || 0} Verbesserungspunkte</p>
            </div>
          </div>
        </div>
      )}
      {/* Quick Actions */}
      <div className="space-y-1">
        <p className="text-xs text-[#999] uppercase tracking-wider font-medium mb-2">{t("creator_helper.quick_actions", { defaultValue: "Schnellaktionen" })}</p>
        {[
          { i18nKey: "creator_helper.qa_audit", fallback: "Profil-Audit starten", tab: "audit" },
          { i18nKey: "creator_helper.qa_textgen", fallback: "Text generieren", tab: "textgen" },
          { i18nKey: "creator_helper.qa_tags", fallback: "Tags optimieren", tab: "tags" },
          { i18nKey: "creator_helper.qa_wizard", fallback: "Coaching-Wizard", tab: "wizard" },
        ].map((a) => (
          <button key={a.tab} className="w-full flex items-center justify-between py-2 text-sm text-[#0A0A0A] hover:bg-[#FAFAFA] transition-colors">
            {t(a.i18nKey, { defaultValue: a.fallback })} <ChevronRight size={14} className="text-[#CCC]" />
          </button>
        ))}
      </div>
    </div>
  );
}

function AuditTab() {
  const { t } = useTranslation();
  const [audit, setAudit] = useState(null);
  const [running, setRunning] = useState(false);

  const runAudit = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/studio/helper/audit/profile");
      setAudit(data);
    } catch { /* ignore */ }
    finally { setRunning(false); }
  };

  return (
    <div data-testid="helper-audit" className="space-y-4">
      <button data-testid="run-audit-btn" onClick={runAudit} disabled={running}
        className="w-full rounded-full h-11 bg-[#0A0A0A] text-white text-sm font-medium flex items-center justify-center gap-1.5 transition-colors hover:bg-[#222] active:scale-[0.98] disabled:opacity-50">
        <RefreshCw size={14} className={running ? "animate-spin" : ""} />
        {running ? "Audit läuft..." : "Profil-Audit starten"}
      </button>

      {audit && (
        <>
          {/* Score */}
          <div className="flex items-center gap-4 p-4 border border-[#E5E5E5]">
            <div className="w-20 h-20 rounded-full border-4 border-[#0A0A0A] flex items-center justify-center">
              <span className="text-2xl font-bold text-[#0A0A0A]">{audit.total_score}</span>
            </div>
            <div>
              <p className="text-base font-bold text-[#0A0A0A]">Potenzial: {audit.total_score}%</p>
              <p className="text-xs text-[#999] mt-0.5">{t("creator_helper.opt_potential")}</p>
            </div>
          </div>

          {/* Subscores */}
          <div className="space-y-2">
            <p className="text-xs text-[#999] uppercase tracking-wider font-medium">Einzelbewertungen</p>
            {Object.entries(audit.subscores || {}).map(([key, val]) => (
              <div key={key} className="flex items-center gap-3">
                <span className="text-xs text-[#666] w-28 capitalize">{key.replace(/_/g, " ")}</span>
                <div className="flex-1 h-2 bg-[#E5E5E5]">
                  <div className="h-full bg-[#0A0A0A] transition-all" style={{ width: `${val}%` }} />
                </div>
                <span className="text-xs font-medium text-[#0A0A0A] w-8 text-right">{val}</span>
              </div>
            ))}
          </div>

          {/* Weaknesses */}
          {audit.weaknesses?.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs text-[#999] uppercase tracking-wider font-medium">{t("creator_helper.top_weaknesses")}</p>
              {audit.weaknesses.map((w, i) => (
                <div key={i} data-testid={`weakness-${i}`} className="p-3 border border-[#E5E5E5] bg-[#FAFAFA]">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium text-[#0A0A0A]">{w.area}</p>
                    <span className="text-xs text-amber-500 font-medium">{w.score}/100</span>
                  </div>
                  <p className="text-xs text-[#666] mt-1">{w.fix}</p>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function TextGenTab({ overview }) {
  const { t } = useTranslation();
  const [targetType, setTargetType] = useState("POST");
  const [mood, setMood] = useState("neutral");
  const [context, setContext] = useState("");
  const [tags, setTags] = useState("");
  const [variants, setVariants] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState("");

  const generate = async () => {
    if (!context) return;
    setError(""); setGenerating(true);
    try {
      const { data } = await api.post("/studio/helper/generate-text", { target_type: targetType, mood, context, tags });
      setVariants(data.variants || [data.raw]);
    } catch (err) { setError(formatError(err)); }
    finally { setGenerating(false); }
  };

  return (
    <div data-testid="helper-textgen" className="space-y-4">
      <div>
        <label className="text-xs font-medium text-[#666] uppercase tracking-wider">Zieltyp</label>
        <div className="flex gap-2 mt-1">
          {["POST", "STORY", "LISTING", "BIO"].map((t) => (
            <button key={t} onClick={() => setTargetType(t)}
              className={`flex-1 h-9 text-xs font-medium border transition-colors ${targetType === t ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-white text-[#666] border-[#E5E5E5]"}`}>
              {t}
            </button>
          ))}
        </div>
      </div>
      <div>
        <label className="text-xs font-medium text-[#666] uppercase tracking-wider">Stimmung</label>
        <div className="flex gap-2 mt-1 flex-wrap">
          {["neutral", "freundlich", "charmant", "geheimnisvoll", "verkaufsfördernd"].map((m) => (
            <PillChip key={m} active={mood === m} onClick={() => setMood(m)} variant="neutral" size="md">
              {m}
            </PillChip>
          ))}
        </div>
      </div>
      <div>
        <label className="text-xs font-medium text-[#666] uppercase tracking-wider">Kontext / Beschreibung *</label>
        <textarea data-testid="textgen-context" value={context} onChange={(e) => setContext(e.target.value)}
          className="mt-1 w-full h-24 px-4 py-3 border border-[#E5E5E5] text-sm resize-none focus:outline-none focus:ring-1 focus:ring-[#0A0A0A]"
          placeholder={t("creator_helper.text_gen_ph")} />
      </div>
      <div>
        <label className="text-xs font-medium text-[#666] uppercase tracking-wider">Tags (optional)</label>
        <input value={tags} onChange={(e) => setTags(e.target.value)}
          className="mt-1 w-full h-10 px-4 border border-[#E5E5E5] text-sm focus:outline-none focus:ring-1 focus:ring-[#0A0A0A]"
          placeholder="fashion, vintage, elegant..." />
      </div>
      {error && <p className="text-sm text-red-500">{error}</p>}
      <button data-testid="textgen-generate" onClick={generate} disabled={generating || !context}
        className="w-full rounded-full h-11 bg-[#0A0A0A] text-white text-sm font-medium flex items-center justify-center gap-1.5 transition-colors hover:bg-[#222] active:scale-[0.98] disabled:opacity-30">
        <Zap size={14} /> {generating ? "Wird generiert..." : "Textvarianten generieren"}
      </button>
      {/* Quota */}
      {overview && (
        <p className="text-[10px] text-[#CCC] text-center">
          {overview.usage.text_gen_used}/{overview.usage.text_gen_limit} Generierungen diesen Monat ({overview.plan_code})
        </p>
      )}
      {/* Variants */}
      {variants.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs text-[#999] uppercase tracking-wider font-medium">Varianten</p>
          {variants.map((v, i) => (
            <div key={i} data-testid={`variant-${i}`} className="p-3 border border-[#E5E5E5] bg-[#FAFAFA] flex items-start gap-2">
              <p className="flex-1 text-sm text-[#0A0A0A] leading-relaxed">{v}</p>
              <button onClick={() => navigator.clipboard?.writeText(v)} className="shrink-0 px-2 py-1 text-[10px] text-[#999] border border-[#E5E5E5] hover:bg-white transition-colors">
                Kopieren
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function TagsTab() {
  const { t } = useTranslation();
  const [context, setContext] = useState("");
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(false);

  const suggest = async () => {
    if (!context) return;
    setLoading(true);
    try {
      const { data } = await api.post("/studio/helper/suggest-tags", { context });
      setTags(data.tags || []);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };

  return (
    <div data-testid="helper-tags" className="space-y-4">
      <div>
        <label className="text-xs font-medium text-[#666] uppercase tracking-wider">Content beschreiben</label>
        <textarea data-testid="tags-context" value={context} onChange={(e) => setContext(e.target.value)}
          className="mt-1 w-full h-24 px-4 py-3 border border-[#E5E5E5] text-sm resize-none focus:outline-none focus:ring-1 focus:ring-[#0A0A0A]"
          placeholder={t("creator_helper.post_ph")} />
      </div>
      <button data-testid="tags-suggest" onClick={suggest} disabled={loading || !context}
        className="w-full rounded-full h-11 bg-[#0A0A0A] text-white text-sm font-medium transition-colors hover:bg-[#222] active:scale-[0.98] disabled:opacity-30">
        {loading ? "Wird analysiert..." : "Tags vorschlagen"}
      </button>
      {tags.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {tags.map((t, i) => (
            <span key={i} data-testid={`tag-${i}`} className="px-3 py-1 bg-[#F5F5F5] border border-[#E5E5E5] text-xs text-[#0A0A0A]">
              #{t}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function InsightsTab() {
  const { t } = useTranslation();
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/studio/helper/insights");
        setInsights(data);
      } catch { /* ignore */ }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <div className="flex justify-center py-8"><div className="w-5 h-5 border-2 border-[#E5E5E5] border-t-[#0A0A0A] rounded-full animate-spin" /></div>;
  if (!insights) return <p className="text-sm text-[#999] text-center py-8">{t("creator_helper.insights_na")}</p>;

  const stats = [
    { label: "Beiträge", value: insights.total_posts },
    { label: "Stories", value: insights.total_stories },
    { label: "Follower", value: insights.total_followers },
    { label: "Likes erhalten", value: insights.total_likes_received },
    { label: "Aktive Listings", value: insights.active_listings },
    { label: "Engagement Rate", value: `${insights.engagement_rate}%` },
  ];

  return (
    <div data-testid="helper-insights">
      <div className="grid grid-cols-2 gap-3">
        {stats.map((s) => (
          <div key={s.label} className="p-3 border border-[#E5E5E5]">
            <p className="text-[10px] text-[#999] uppercase tracking-wider">{s.label}</p>
            <p className="text-xl font-bold text-[#0A0A0A] mt-1">{s.value}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function WizardTab() {
  const { t } = useTranslation();
  const [step, setStep] = useState(0);
  const [wizardId, setWizardId] = useState(null);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const WIZARD_STEPS = [
    { key: "goal", label: "Was ist dein Hauptziel?", options: ["Reichweite aufbauen", "Produkte verkaufen", "Community aufbauen", "Marke stärken"] },
    { key: "visibility_choice", label: "Wie sichtbar möchtest du sein?", options: ["Sehr öffentlich", "Diskret", "Nur für Follower"] },
    { key: "time_investment", label: "Wie viel Zeit kannst du investieren?", options: ["1-2h/Woche", "3-5h/Woche", "Täglich", "Vollzeit"] },
    { key: "content_focus", label: "Welcher Content-Typ passt zu dir?", options: ["Fotos", "Videos", "Texte", "Mix aus allem"] },
  ];

  const startWizard = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/studio/helper/wizard", { domain: "ST" });
      setWizardId(data.wizard_id);
      setStep(0);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };

  const answerStep = async (answer) => {
    if (!wizardId) return;
    const currentStep = WIZARD_STEPS[step];
    setAnswers(prev => ({ ...prev, [currentStep.key]: answer }));
    try {
      const { data } = await api.post(`/studio/helper/wizard/${wizardId}/answer`, { step: currentStep.key, answer });
      if (data.status === "COMPLETED") {
        // Load final result
        const { data: session } = await api.get("/studio/helper/wizard");
        setResult(session.session);
      } else {
        setStep(step + 1);
      }
    } catch { /* ignore */ }
  };

  if (result) {
    return (
      <div data-testid="wizard-result" className="space-y-4">
        <div className="p-4 border border-[#E5E5E5] bg-[#FAFAFA]">
          <p className="text-xs text-[#999] uppercase tracking-wider font-medium mb-2">{t("creator_helper.your_rec")}</p>
          <p className="text-sm text-[#0A0A0A] leading-relaxed">{result.recommended_setup}</p>
        </div>
        <button onClick={() => { setResult(null); setWizardId(null); setStep(0); setAnswers({}); }}
          className="w-full h-10 border border-[#E5E5E5] text-sm text-[#666] hover:bg-[#F5F5F5] transition-colors">
          Wizard erneut starten
        </button>
      </div>
    );
  }

  if (!wizardId) {
    return (
      <div data-testid="wizard-start" className="text-center py-8">
        <Sparkles size={32} className="mx-auto text-[#999] mb-3" />
        <h3 className="text-base font-bold text-[#0A0A0A]">Coaching Wizard</h3>
        <p className="text-sm text-[#999] mt-1 mb-4">4 Fragen. Personalisierte Empfehlung.</p>
        <button data-testid="wizard-start-btn" onClick={startWizard} disabled={loading}
          className="rounded-full h-11 px-8 bg-[#0A0A0A] text-white text-sm font-medium transition-colors hover:bg-[#222] active:scale-[0.98] disabled:opacity-50">
          {loading ? "Wird gestartet..." : "Wizard starten"}
        </button>
      </div>
    );
  }

  const currentStep = WIZARD_STEPS[step];
  return (
    <div data-testid="wizard-step" className="space-y-4">
      {/* Progress */}
      <div className="flex gap-1">
        {WIZARD_STEPS.map((_, i) => (
          <div key={i} className={`flex-1 h-1 ${i <= step ? "bg-[#0A0A0A]" : "bg-[#E5E5E5]"}`} />
        ))}
      </div>
      <p className="text-xs text-[#999]">Schritt {step + 1} von {WIZARD_STEPS.length}</p>
      <h3 className="text-base font-bold text-[#0A0A0A]">{currentStep.label}</h3>
      <div className="space-y-2">
        {currentStep.options.map((opt) => (
          <button key={opt} data-testid={`wizard-option-${opt.replace(/\s/g, "-").toLowerCase()}`}
            onClick={() => answerStep(opt)}
            className={`w-full flex items-center justify-between px-4 py-3 border transition-colors text-left text-sm ${
              answers[currentStep.key] === opt ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "bg-white text-[#0A0A0A] border-[#E5E5E5] hover:border-[#999]"
            }`}>
            {opt}
            {answers[currentStep.key] === opt && <Check size={14} />}
          </button>
        ))}
      </div>
    </div>
  );
}

function ContentScoreTab() {
  const { t } = useTranslation();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scoring, setScoring] = useState(null);
  const [score, setScore] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/studio/social/posts");
        setPosts(data.posts || []);
      } catch { setPosts([]); }
      finally { setLoading(false); }
    })();
  }, []);

  const scoreContent = async (contentId, contentType) => {
    setScoring(contentId);
    setScore(null);
    try {
      const { data } = await api.post("/studio/helper/content-score", { content_type: contentType, content_id: contentId });
      setScore(data);
    } catch { /* ignore */ }
    finally { setScoring(null); }
  };

  if (loading) return <div className="flex justify-center py-8"><div className="w-5 h-5 border-2 border-[#E5E5E5] border-t-[#0A0A0A] rounded-full animate-spin" /></div>;

  return (
    <div data-testid="helper-content-score" className="space-y-4">
      <p className="text-xs text-[#999] uppercase tracking-wider font-medium">{t("creator_helper.analyze_post")}</p>
      {posts.length === 0 ? (
        <p className="text-sm text-[#999] text-center py-8">{t("creator_helper.no_posts")}</p>
      ) : (
        <div className="space-y-2">
          {posts.map((p) => (
            <div key={p.post_id} data-testid={`score-item-${p.post_id}`} className="p-3 border border-[#E5E5E5] flex items-center gap-3">
              <div className="flex-1 min-w-0">
                <p className="text-sm text-[#0A0A0A] truncate">{p.text}</p>
                <p className="text-[10px] text-[#999] mt-0.5">{new Date(p.created_at).toLocaleDateString("de-DE")}</p>
              </div>
              <button
                data-testid={`score-btn-${p.post_id}`}
                onClick={() => scoreContent(p.post_id, "POST")}
                disabled={scoring === p.post_id}
                className="shrink-0 h-8 px-3 bg-[#0A0A0A] text-white text-[10px] font-medium flex items-center gap-1 disabled:opacity-40"
              >
                <Star size={10} /> {scoring === p.post_id ? "…" : t("creator_helper.score_btn", { defaultValue: "Bewerten" })}
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Score Result */}
      {score && (
        <div data-testid="content-score-result" className="space-y-3 mt-4">
          <div className="flex items-center gap-4 p-4 border border-[#E5E5E5]">
            <div className="w-16 h-16 rounded-full border-4 border-[#0A0A0A] flex items-center justify-center">
              <span className="text-xl font-bold text-[#0A0A0A]">{score.total_score}</span>
            </div>
            <div>
              <p className="text-sm font-bold text-[#0A0A0A]">Content Score: {score.total_score}/100</p>
              <p className="text-xs text-[#999] mt-0.5">{score.content_type === "POST" ? "Feed Post" : "Story"}</p>
            </div>
          </div>

          {/* Subscores */}
          <div className="space-y-2">
            <p className="text-xs text-[#999] uppercase tracking-wider font-medium">Einzelbewertungen</p>
            {Object.entries(score.subscores || {}).map(([key, val]) => (
              <div key={key} className="flex items-center gap-3">
                <span className="text-xs text-[#666] w-28 capitalize">{key.replace(/_/g, " ")}</span>
                <div className="flex-1 h-2 bg-[#E5E5E5]">
                  <div className="h-full bg-[#0A0A0A] transition-all" style={{ width: `${val}%` }} />
                </div>
                <span className="text-xs font-medium text-[#0A0A0A] w-8 text-right">{val}</span>
              </div>
            ))}
          </div>

          {/* Suggestions */}
          {score.suggestions?.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs text-[#999] uppercase tracking-wider font-medium">{t("creator_helper.suggestions")}</p>
              {score.suggestions.map((s, i) => (
                <div key={i} data-testid={`score-suggestion-${i}`} className="p-3 border border-[#E5E5E5] bg-[#FAFAFA] flex items-start gap-2">
                  <Zap size={12} className="text-amber-500 mt-0.5 shrink-0" />
                  <p className="text-xs text-[#666] leading-relaxed">{s}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

